import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from './supabase';
import { PickerSelect } from './ui/PickerSelect';
import { Thumb } from './ui/Thumb';
import { DataGrid, GridCol } from './ui/DataGrid';
import { cn } from './ui/Table';
import { RefreshCw, ChevronRight, CheckCircle, Plus, Trash2, Pencil, Check, X, Layers } from 'lucide-react';
import { Providers } from './Providers';
// Componentes de uiarc.dev (ver src/ui/arc)
import { Button } from './ui/arc/button';
import { Badge } from './ui/arc/badge';
import { Input } from './ui/arc/input';
import { MetricCard } from './ui/arc/metric-card';
import { ChipGroup } from './ui/arc/chip-group';
import { Checkbox } from './ui/arc/checkbox';
import { Dialog, DialogContent } from './ui/arc/dialog';
import './Products.css';

// Catálogo — productos de Tiendanube (+ manuales) con costo, precio y margen por medio
// de pago. UI: todo dentro de `.tw` (tokens + preflight acotado de uiarc, ver
// src/ui/arc/foundation.css), con componentes de src/ui y src/ui/arc. Misma receta que
// Insumos (pantalla de referencia).

// Control compacto con las medidas del `input` de uiarc (radio, foco por borde, sin anillo).
// Para celdas y editores densos, donde el Input con label de uiarc no entra.
const CTL = 'h-8 w-full min-w-0 rounded-[var(--radius-control)] border border-line bg-card px-2 '
    + 'text-[length:var(--text-sm)] tabular-nums text-ink transition-colors placeholder:text-faint '
    + 'hover:border-[var(--border-strong)] focus:border-[var(--foreground)] focus:outline-none';
// Select a altura de control "md", para formularios (dialogs).
const SELECT = 'h-[var(--control-height-md)] w-full min-w-0 rounded-[var(--radius-control)] border border-[var(--border-strong)] '
    + 'bg-card px-3 text-[length:var(--text-sm)] text-ink transition-colors hover:border-[var(--foreground)] focus:border-[var(--foreground)] focus:outline-none';

/** Campo con label arriba, igual que el Input de uiarc, para selects y pickers. */
function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
    return (
        <div className={cn('grid min-w-0 content-start gap-2', className)}>
            <span className="text-[length:var(--text-sm)] font-medium text-ink">{label}</span>
            {children}
        </div>
    );
}

/** Input numérico chico con su label (medidas y cantidad de un insumo). */
// Precio del INSUMO editable desde la receta del producto (13/10/2026). Es el precio del insumo
// (cost_materials.unit_cost), no del producto: cambia en todos los productos que lo usan. Se guarda
// al salir del campo o con Enter. Los meses ya vendidos no cambian (versionado, migr 103).
function MatPriceField({ mat, usedIn, onSaved }: { mat: { id: number; unit_cost: number; currency: string; type: string }; usedIn: number; onSaved: (n: number) => void }) {
    const [draft, setDraft] = useState(String(mat.unit_cost ?? ''));
    const [state, setState] = useState<'idle' | 'saving' | 'ok' | 'error'>('idle');
    useEffect(() => { setDraft(String(mat.unit_cost ?? '')); }, [mat.unit_cost]);
    const save = async () => {
        const n = Number(String(draft).replace(',', '.'));
        if (!Number.isFinite(n) || n < 0) { setDraft(String(mat.unit_cost)); return; }
        if (n === Number(mat.unit_cost)) return;
        setState('saving');
        const { error } = await supabase.from('cost_materials').update({ unit_cost: n }).eq('id', mat.id);
        if (error) { setState('error'); return; }
        onSaved(n); setState('ok');
        setTimeout(() => setState(s => (s === 'ok' ? 'idle' : s)), 1600);
    };
    return (
        <label className="grid min-w-0 gap-1 text-[length:var(--text-xs)] text-faint"
            title={`Precio del insumo (${mat.currency}${mat.type === 'acrylic' ? ' por kg' : ' por unidad'}). Cambia en ${usedIn > 1 ? `los ${usedIn} productos que lo usan` : 'todos los productos que lo usen'}; lo ya vendido no cambia.`}>
            <span className="flex items-center justify-between gap-1">
                Precio {mat.currency === 'ARS' ? '$' : 'U$D'}{mat.type === 'acrylic' ? '/kg' : '/u'}
                <span className={cn(state === 'error' ? 'text-[var(--danger)]' : state === 'ok' ? 'text-[var(--success)]' : 'text-faint')}>
                    {state === 'saving' ? 'guardando…' : state === 'ok' ? '✓' : state === 'error' ? '✗ error' : usedIn > 1 ? `en ${usedIn}` : ''}
                </span>
            </span>
            <input type="number" step="0.01" inputMode="decimal" value={draft}
                onChange={e => setDraft(e.target.value)} onBlur={save}
                onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') { setDraft(String(mat.unit_cost)); (e.target as HTMLInputElement).blur(); } }}
                className={cn(CTL, 'text-right')} />
        </label>
    );
}

function NumField({ label, value, onChange, step }: { label: string; value: number | string; onChange: (n: number) => void; step?: string }) {
    return (
        <label className="grid min-w-0 gap-1 text-[length:var(--text-xs)] text-faint">
            {label}
            <input type="number" step={step} value={value} onChange={e => onChange(Number(e.target.value))} className={cn(CTL, 'text-right')} />
        </label>
    );
}

type VariantDraft = { id?: number | null; values: string; sku: string; price: string; stock: string };

/** Editor de variantes de los dialogs de alta y edición de producto manual. */
function VariantsEditor({ rows, onChange, minRows = 0 }: { rows: VariantDraft[]; onChange: (rows: VariantDraft[]) => void; minRows?: number }) {
    const set = (i: number, patch: Partial<VariantDraft>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
    return (
        <div className="grid gap-2">
            <span className="text-[length:var(--text-sm)] font-medium text-ink">Variantes ({rows.length})</span>
            <div className="overflow-x-auto rounded-[var(--radius-control)] border border-line">
                <table className="w-full min-w-[30rem] text-[length:var(--text-sm)]">
                    <thead>
                        <tr className="border-b border-line text-left text-faint">
                            <th className="h-9 px-2 font-medium">Variante (ej: Blanco, Grande)</th>
                            <th className="h-9 w-28 px-2 font-medium">SKU</th>
                            <th className="h-9 w-28 px-2 text-right font-medium">Precio</th>
                            <th className="h-9 w-20 px-2 text-right font-medium">Stock</th>
                            <th className="w-10" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((v, i) => (
                            <tr key={v.id ?? `new-${i}`} className="border-b border-[var(--border-subtle)] last:border-0">
                                <td className="p-1.5"><input className={CTL} value={v.values} placeholder="Blanco, Grande" onChange={e => set(i, { values: e.target.value })} /></td>
                                <td className="p-1.5"><input className={CTL} value={v.sku} placeholder="Opcional" onChange={e => set(i, { sku: e.target.value })} /></td>
                                <td className="p-1.5"><input className={cn(CTL, 'text-right')} type="number" min="0" value={v.price} placeholder="0" onChange={e => set(i, { price: e.target.value })} /></td>
                                <td className="p-1.5"><input className={cn(CTL, 'text-right')} type="number" min="0" value={v.stock} placeholder="0" onChange={e => set(i, { stock: e.target.value })} /></td>
                                <td className="p-1.5 text-right">
                                    {rows.length > minRows && (
                                        <Button variant="ghost" size="sm" aria-label="Quitar variante" onClick={() => onChange(rows.filter((_, j) => j !== i))}><Trash2 size={14} /></Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div>
                <Button variant="ghost" size="sm" onClick={() => onChange([...rows, { id: null, values: '', sku: '', price: '', stock: '1' }])}>
                    <Plus size={14} /> Agregar variante
                </Button>
            </div>
        </div>
    );
}

/** Color del margen: verde si gana, rojo si pierde, apagado si no hay precio. */
const marginCls = (has: boolean, positive: boolean) =>
    !has ? 'text-faint' : positive ? 'text-[var(--success)]' : 'text-[var(--danger)]';

type Flag = 'sin_costo' | 'negativo' | 'envio' | 'stock';
const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

interface TnProduct {
    id: number;
    name: string;
    image_url: string | null;
    synced_at: string | null;
    active: boolean;
    markup: number;
    has_free_shipping?: boolean;
    is_stock?: boolean;
    lead_days?: number | null;   // días de fabricación (null = default de cost_settings)
}

interface TnVariant {
    id: number;
    product_id: number;
    sku: string | null;
    price: number | null;
    promotional_price: number | null;
    stock: number | null;
    values: string[];
}

interface Material {
    id: number;
    name: string;
    type: 'simple' | 'acrylic';
    unit_cost: number;
    currency: 'ARS' | 'USD';
    provider_id?: number | null;
    width_mm?: number;
    height_mm?: number;
    thickness_mm?: number;
    weight_kg?: number;
    image_url?: string | null;
    group_id?: string | null;
}

interface Provider {
    id: number;
    name: string;
}

interface CostSettings {
    id: number;
    usd_rate: number;
    iva_percent: number;
    acrylic_waste_percent: number;
    usd_rate_updated_at?: string;
}

interface TnProductMaterial {
    id: number;
    tn_product_id: number;
    material_id: number;
    quantity: number;
    width_mm?: number;
    height_mm?: number;
    thickness_mm?: number;
}

interface TnVariantMaterial {
    id: number;
    tn_variant_id: number;
    material_id: number;
    quantity: number;
    width_mm?: number;
    height_mm?: number;
    thickness_mm?: number;
}

export function Products({ showSnackbar, section = 'catalog' }: { showSnackbar: (msg: string, type: 'success' | 'error') => void; section?: 'catalog' | 'providers' }) {
    // La sección la manda App: hay UNA sola barra de tabs (Catálogo / Insumos /
    // Proveedores / Publicaciones ML / Mayorista) y cada una tiene su URL. Antes había
    // dos barras superpuestas y "Insumos" aparecía en las dos, apuntando a pantallas
    // distintas (la vieja base de insumos de acá y el módulo /insumos).
    const activeTab: 'catalog' | 'providers' = section === 'providers' ? 'providers' : 'catalog';

    // Catalog State
    const [products, setProducts] = useState<TnProduct[]>([]);
    const [variants, setVariants] = useState<TnVariant[]>([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [productDiscounts, setProductDiscounts] = useState<Record<number, number>>({});

    // Materials State
    const [materials, setMaterials] = useState<Material[]>([]);
    // insumo elegido en cada picker, por (tipo + id del padre)
    const [matPick, setMatPick] = useState<Record<string, number | null>>({});
    // imagen del insumo: la propia, o la del grupo padre si la variante no tiene
    const [supplyGroupImgs, setSupplyGroupImgs] = useState<Record<string, string>>({});
    const materialImg = (m: Material) => m.image_url || (m.group_id ? supplyGroupImgs[m.group_id] : null) || null;
    const [providers, setProviders] = useState<Provider[]>([]);

    // Cost Calculator & Assignation State
    const [settings, setSettings] = useState<CostSettings | null>(null);
    const [productMaterials, setProductMaterials] = useState<TnProductMaterial[]>([]);
    const [variantMaterials, setVariantMaterials] = useState<TnVariantMaterial[]>([]);
    // en cuántos productos se usa cada insumo (para avisar al editar su precio desde una receta)
    const matUsage = useMemo(() => {
        const prodOfVariant = new Map<number, number>(variants.map(v => [v.id, v.product_id] as [number, number]));
        const sets = new Map<number, Set<number>>();
        const add = (mid: number, pid: number | undefined) => { if (pid == null) return; if (!sets.has(mid)) sets.set(mid, new Set()); sets.get(mid)!.add(pid); };
        productMaterials.forEach(pm => add(pm.material_id, pm.tn_product_id));
        variantMaterials.forEach(vm => add(vm.material_id, prodOfVariant.get(vm.tn_variant_id)));
        const out = new Map<number, number>(); sets.forEach((v, k) => out.set(k, v.size)); return out;
    }, [productMaterials, variantMaterials, variants]);
    const [editingProductMatsId, setEditingProductMatsId] = useState<number | null>(null);
    const [editingProductMats, setEditingProductMats] = useState<TnProductMaterial[]>([]);
    const [editingVariantId, setEditingVariantId] = useState<number | null>(null);
    const [editingVariantMats, setEditingVariantMats] = useState<TnVariantMaterial[]>([]);
    const [applyToValue, setApplyToValue] = useState<string>('');
    // Anti doble-click: los guardados son delete+insert — dos clicks en paralelo duplican.
    const [savingMats, setSavingMats] = useState(false);
    // Costo directo (reventa): productos que se compran y venden como están.
    const [directCostDraft, setDirectCostDraft] = useState<Record<number, string>>({});

    const [directCurrencyDraft, setDirectCurrencyDraft] = useState<Record<number, string>>({});
    const handleSaveDirectCost = async (productId: number) => {
        const raw = (directCostDraft[productId] ?? '').trim();
        const val = raw === '' ? null : Number(raw);
        const cur = directCurrencyDraft[productId] || (products.find(pr => pr.id === productId) as any)?.direct_cost_currency || 'ARS';
        const { error } = await supabase.from('tn_products').update({ direct_cost: val, direct_cost_currency: cur }).eq('id', productId);
        if (error) { showSnackbar('No se pudo guardar el costo directo', 'error'); return; }
        setProducts(products.map(pr => pr.id === productId ? { ...pr, direct_cost: val, direct_cost_currency: cur } as any : pr));
        showSnackbar(val != null ? `Costo directo guardado (${cur}) — pisa el cálculo por insumos` : 'Costo directo quitado — vuelve a usar insumos', 'success');
    };

    // Costo directo por VARIANTE (prioridad sobre el del producto)
    const [variantDirectDraft, setVariantDirectDraft] = useState<Record<number, string>>({});
    const [variantDirectCurrency, setVariantDirectCurrency] = useState<Record<number, string>>({});
    const handleSaveVariantDirectCost = async (variantId: number) => {
        const raw = (variantDirectDraft[variantId] ?? '').trim();
        const val = raw === '' ? null : Number(raw);
        const cur = variantDirectCurrency[variantId] || (variants.find(vv => vv.id === variantId) as any)?.direct_cost_currency || 'ARS';
        const { error } = await supabase.from('tn_variants').update({ direct_cost: val, direct_cost_currency: cur }).eq('id', variantId);
        if (error) { showSnackbar('No se pudo guardar el costo directo', 'error'); return; }
        setVariants(variants.map(vv => vv.id === variantId ? { ...vv, direct_cost: val, direct_cost_currency: cur } as any : vv));
        showSnackbar(val != null ? `Costo directo de la variante guardado (${cur})` : 'Costo directo de la variante quitado', 'success');
    };

    // Manual Product Modal State
    const [showNewProductModal, setShowNewProductModal] = useState(false);
    const [npName, setNpName] = useState('');
    const [npImageUrl, setNpImageUrl] = useState('');
    const [npVariants, setNpVariants] = useState<{ values: string; sku: string; price: string; stock: string }[]>([
        { values: '', sku: '', price: '', stock: '1' }
    ]);

    // Edit Product Modal State
    const [editingProduct, setEditingProduct] = useState<TnProduct | null>(null);
    const [epName, setEpName] = useState('');
    const [epImageUrl, setEpImageUrl] = useState('');
    const [epVariants, setEpVariants] = useState<{ id: number | null; values: string; sku: string; price: string; stock: string }[]>([]);

    // Quick Material Modal State
    const [showQuickMaterialModal, setShowQuickMaterialModal] = useState(false);
    const [qmNewProvOpen, setQmNewProvOpen] = useState(false);
    const [qmNewProvName, setQmNewProvName] = useState('');
    const addProviderInlineQm = async () => {
        const name = qmNewProvName.trim();
        if (!name) return;
        const { data, error } = await supabase.from('providers').insert({ name }).select().single();
        if (error || !data) { showSnackbar('No se pudo crear el proveedor', 'error'); return; }
        setProviders(ps => [...ps, data].sort((a: any, b: any) => a.name.localeCompare(b.name)));
        setQmProvider(String(data.id));
        setQmNewProvOpen(false);
        setQmNewProvName('');
        showSnackbar(`Proveedor "${name}" creado`, 'success');
    };
    const [qmName, setQmName] = useState('');
    const [qmType, setQmType] = useState<'simple' | 'acrylic'>('simple');
    const [qmCost, setQmCost] = useState('');
    const [qmCurrency, setQmCurrency] = useState<'ARS' | 'USD'>('ARS');
    const [qmProvider, setQmProvider] = useState<string>('');
    const [qmTarget, setQmTarget] = useState<{ type: 'product' | 'variant', id: number } | null>(null);

    useEffect(() => {
        fetchData();
    }, []);

    async function fetchData() {
        setLoading(true);
        try {
            const [prodRes, varRes, matRes, sgRes, provRes, setRes, pmatRes, vmatRes] = await Promise.all([
                supabase.from('tn_products').select('*').eq('active', true).order('name'),
                (async () => {
                    // Paginar tn_variants (hard cap 1000 por request en PostgREST).
                    const all: any[] = [];
                    for (let offset = 0; offset < 20000; offset += 1000) {
                        const { data, error } = await supabase
                            .from('tn_variants')
                            .select('*')
                            .order('id', { ascending: true })
                            .range(offset, offset + 999);
                        if (error) return { data: null, error };
                        if (!data || data.length === 0) break;
                        all.push(...data);
                        if (data.length < 1000) break;
                    }
                    return { data: all, error: null as any };
                })(),
                supabase.from('cost_materials').select('*').order('name'),
                supabase.from('supply_groups').select('id, image_url'),
                supabase.from('providers').select('id, name').order('name'),
                supabase.from('cost_settings').select('*').single(),
                supabase.from('tn_product_materials').select('*'),
                supabase.from('tn_variant_materials').select('*')
            ]);

            if (prodRes.error) throw prodRes.error;
            if (varRes.error) throw varRes.error;

            setSupplyGroupImgs(Object.fromEntries(((sgRes.data || []) as any[])
                .filter(g => g.image_url).map(g => [g.id, g.image_url as string])));
            setProducts(prodRes.data || []);
            setVariants((varRes.data || []).map((v: any) => ({
                ...v,
                values: typeof v.values === 'string' ? JSON.parse(v.values) : (v.values || [])
            })));

            if (matRes.data) setMaterials(matRes.data);
            if (provRes.data) setProviders(provRes.data);
            if (setRes.data) setSettings(setRes.data);
            if (pmatRes.data) setProductMaterials(pmatRes.data);
            if (vmatRes.data) setVariantMaterials(vmatRes.data);
        } catch (err: any) {
            console.error('Error fetching data:', err);
            showSnackbar('Error al cargar datos', 'error');
        } finally {
            setLoading(false);
        }
    }

    const handleSync = async () => {
        setSyncing(true);
        try {
            const response = await fetch(`${(process.env.REACT_APP_SUPABASE_URL || '').trim()}/functions/v1/sync-tiendanube`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });
            const result = await response.json();
            if (response.ok) {
                showSnackbar('Sincronización exitosa', 'success');
                fetchData();
            } else {
                showSnackbar(`Error: ${result.error}`, 'error');
            }
        } catch (error) {
            showSnackbar('Error de conexión', 'error');
        } finally {
            setSyncing(false);
        }
    };

    // Manual Product Handler
    const handleAddProduct = async () => {
        if (!npName.trim()) {
            showSnackbar('Ingresá el nombre del producto', 'error');
            return;
        }
        const hasValidVariant = npVariants.some(v => v.price);
        if (!hasValidVariant) {
            showSnackbar('Agregá al menos una variante con precio', 'error');
            return;
        }
        try {
            // Generate unique IDs for manual products (9XXXXXXXXX range to avoid TN collisions)
            const productId = 9000000000 + Math.floor(Math.random() * 999999999);

            const { error: prodErr } = await supabase
                .from('tn_products')
                .insert({
                    id: productId,
                    name: npName.trim(),
                    image_url: npImageUrl.trim() || null,
                    active: true,
                    markup: 100,
                    has_free_shipping: false,
                });

            if (prodErr) throw prodErr;

            const variantsToInsert = npVariants
                .filter(v => v.price)
                .map(v => ({
                    id: 9000000000 + Math.floor(Math.random() * 999999999),
                    product_id: productId,
                    sku: v.sku.trim() || null,
                    price: parseFloat(v.price) || 0,
                    promotional_price: null,
                    stock: parseInt(v.stock) || 0,
                    values: v.values ? v.values.split(',').map(s => s.trim()).filter(Boolean) : [],
                }));

            if (variantsToInsert.length > 0) {
                const { error: varErr } = await supabase.from('tn_variants').insert(variantsToInsert);
                if (varErr) throw varErr;
            }

            showSnackbar(`Producto "${npName}" creado con ${variantsToInsert.length} variante(s)`, 'success');
            setShowNewProductModal(false);
            setNpName('');
            setNpImageUrl('');
            setNpVariants([{ values: '', sku: '', price: '', stock: '1' }]);
            fetchData();
        } catch (err: any) {
            console.error('Error creating product:', err);
            showSnackbar(`Error: ${err.message}`, 'error');
        }
    };

    // Edit Product Handlers
    const openEditProduct = (product: TnProduct) => {
        const pvs = variants.filter(v => v.product_id === product.id);
        setEditingProduct(product);
        setEpName(product.name);
        setEpImageUrl(product.image_url || '');
        setEpVariants(pvs.map(v => ({
            id: v.id,
            values: Array.isArray(v.values) ? v.values.join(', ') : '',
            sku: v.sku || '',
            price: String(v.price || ''),
            stock: String(v.stock || '0'),
        })));
    };

    const handleSaveEditProduct = async () => {
        if (!editingProduct || !epName.trim()) {
            showSnackbar('El nombre no puede estar vacío', 'error');
            return;
        }
        try {
            // Update product
            const { error: prodErr } = await supabase
                .from('tn_products')
                .update({ name: epName.trim(), image_url: epImageUrl.trim() || null })
                .eq('id', editingProduct.id);
            if (prodErr) throw prodErr;

            // Existing variant IDs
            const existingIds = epVariants.filter(v => v.id !== null).map(v => v.id!);
            const currentVariantIds = variants.filter(v => v.product_id === editingProduct.id).map(v => v.id);

            // Delete removed variants
            const toDelete = currentVariantIds.filter(id => !existingIds.includes(id));
            if (toDelete.length > 0) {
                const { error: delErr } = await supabase.from('tn_variants').delete().in('id', toDelete);
                if (delErr) throw delErr;
            }

            // Update existing variants
            for (const v of epVariants.filter(v => v.id !== null)) {
                const { error: updErr } = await supabase.from('tn_variants').update({
                    sku: v.sku.trim() || null,
                    price: parseFloat(v.price) || 0,
                    stock: parseInt(v.stock) || 0,
                    values: v.values ? v.values.split(',').map(s => s.trim()).filter(Boolean) : [],
                }).eq('id', v.id!);
                if (updErr) throw updErr;
            }

            // Insert new variants (id === null means it's a new row)
            const newVars = epVariants.filter(v => v.id === null);
            if (newVars.length > 0) {
                const toInsert = newVars.map(v => ({
                    id: 9000000000 + Math.floor(Math.random() * 999999999),
                    product_id: editingProduct.id,
                    sku: v.sku.trim() || null,
                    price: parseFloat(v.price) || 0,
                    promotional_price: null,
                    stock: parseInt(v.stock) || 0,
                    values: v.values ? v.values.split(',').map(s => s.trim()).filter(Boolean) : [],
                }));
                console.log('Inserting new variants:', toInsert);
                const { error: insErr } = await supabase.from('tn_variants').insert(toInsert);
                if (insErr) throw insErr;
            }

            showSnackbar('Producto actualizado', 'success');
            setEditingProduct(null);
            fetchData();
        } catch (err: any) {
            console.error('Error updating product:', err);
            showSnackbar(`Error: ${err.message}`, 'error');
        }
    };

    const handleDeleteVariant = async (variantId: number, label: string) => {
        if (!window.confirm(`¿Eliminar la variante "${label}"? Esta acción es local del dashboard, no afecta a Tiendanube.`)) return;
        const { error: matsErr } = await supabase.from('tn_variant_materials').delete().eq('tn_variant_id', variantId);
        if (matsErr) {
            console.error(matsErr);
            showSnackbar('Error al eliminar insumos de la variante', 'error');
            return;
        }
        const { error } = await supabase.from('tn_variants').delete().eq('id', variantId);
        if (error) {
            console.error(error);
            showSnackbar('Error al eliminar la variante', 'error');
            return;
        }
        setVariants(prev => prev.filter(v => v.id !== variantId));
        setVariantMaterials(prev => prev.filter(vm => vm.tn_variant_id !== variantId));
        if (editingVariantId === variantId) setEditingVariantId(null);
        showSnackbar('Variante eliminada', 'success');
    };

    const handleSaveQuickMaterial = async () => {
        if (!qmName || !qmCost) return;
        const newMat = {
            name: qmName,
            type: qmType,
            unit_cost: Number(qmCost),
            currency: qmCurrency,
            provider_id: qmProvider ? Number(qmProvider) : null
        };
        const { data, error } = await supabase.from('cost_materials').insert(newMat).select().single();
        if (data && !error) {
            const addedMat = data as Material;
            setMaterials([...materials, addedMat]);
            setQmName('');
            setQmCost('');
            setQmProvider('');
            setShowQuickMaterialModal(false);
            showSnackbar('Insumo creado y disponible', 'success');

            // Auto-seleccionar y AGREGAR al editor que originó la creación
            if (qmTarget) {
                const newItem: any = {
                    material_id: addedMat.id,
                    quantity: 1,
                    thickness_mm: 4,
                    width_mm: addedMat.type === 'acrylic' ? 100 : undefined,
                    height_mm: addedMat.type === 'acrylic' ? 100 : undefined
                };

                if (qmTarget.type === 'product') {
                    newItem.tn_product_id = qmTarget.id;
                    setEditingProductMats([...editingProductMats, newItem]);
                } else {
                    newItem.tn_variant_id = qmTarget.id;
                    setEditingVariantMats([...editingVariantMats, newItem]);
                }
            }
        } else {
            console.error(error);
            showSnackbar('Error al crear el insumo', 'error');
        }
    };

    const calcVariantCost = (vMats: TnVariantMaterial[], pMats: TnProductMaterial[] = []) => {
        if (!settings) return { cost: 0 };

        let totalCost = 0;
        const allMats = [...vMats, ...(pMats || [])] as any[];

        // Constantes de referencia para acrílico
        const REF_SHEET_WIDTH = 1290;
        const REF_SHEET_HEIGHT = 1860;
        const REF_AREA = REF_SHEET_WIDTH * REF_SHEET_HEIGHT;
        const DENSITY_FACTOR = 11.8 / 4; // 2.95 kg por cada mm de espesor en una plancha de 1290x1860

        allMats.forEach(item => {
            const mat = materials.find(m => m.id === item.material_id);
            if (!mat) return;
            if (mat.type === 'simple') {
                let unitCost = mat.unit_cost;
                if (mat.currency === 'USD') unitCost *= settings.usd_rate;
                totalCost += unitCost * item.quantity;
            } else if (mat.type === 'acrylic') {
                const thickness = item.thickness_mm || 4;
                const weightPerSheet = DENSITY_FACTOR * thickness;
                let ppk = mat.unit_cost; // Precio por Kilo
                if (mat.currency === 'USD') ppk *= settings.usd_rate;

                const costPerSheet = ppk * weightPerSheet * (1 + settings.iva_percent / 100);
                const costPerMm2 = costPerSheet / REF_AREA;

                const pieceArea = (item.width_mm || 0) * (item.height_mm || 0);
                const areaWithWaste = pieceArea * (1 + settings.acrylic_waste_percent / 100);

                totalCost += costPerMm2 * areaWithWaste * item.quantity;
            }
        });

        return { cost: totalCost };
    };

    const handleUpdateProductMaterials = async (productId: number, newMaterials: TnProductMaterial[]) => {
        if (savingMats) return;
        setSavingMats(true);
        try {
        await supabase.from('tn_product_materials').delete().eq('tn_product_id', productId);
        if (newMaterials.length > 0) {
            const matsToInsert = newMaterials.map(m => ({
                tn_product_id: productId,
                material_id: m.material_id,
                quantity: m.quantity,
                width_mm: m.width_mm || null,
                height_mm: m.height_mm || null,
                thickness_mm: m.thickness_mm || 4,
            }));
            await supabase.from('tn_product_materials').insert(matsToInsert);
        }
        setEditingProductMatsId(null);
        fetchData();
        showSnackbar('Insumos base actualizados', 'success');
        } finally {
            setSavingMats(false);
        }
    };
    const handleUpdateProductFreeShipping = async (productId: number, hasFreeShipping: boolean) => {
        const { error } = await supabase
            .from('tn_products')
            .update({ has_free_shipping: hasFreeShipping })
            .eq('id', productId);

        if (!error) {
            setProducts(products.map(p => p.id === productId ? { ...p, has_free_shipping: hasFreeShipping } : p));
        } else {
            console.error(error);
            showSnackbar('Error al actualizar envío', 'error');
        }
    };

    // Días de fabricación del producto → fecha de listo de los pedidos nuevos (migr 098):
    // compra + el producto más lento del pedido. Vacío = el default general.
    const [defaultLeadDays, setDefaultLeadDays] = useState<number>(7);
    useEffect(() => {
        supabase.from('cost_settings').select('default_lead_days').eq('id', 1).maybeSingle()
            .then(({ data }) => { if (data?.default_lead_days != null) setDefaultLeadDays(Number(data.default_lead_days)); });
    }, []);
    const handleUpdateLeadDays = async (productId: number, raw: string) => {
        const v = raw.trim() === '' ? null : Math.max(0, Math.round(Number(raw)));
        if (v != null && !Number.isFinite(v)) return;
        const prev = products.find(p => p.id === productId)?.lead_days ?? null;
        if (prev === v) return;
        setProducts(ps => ps.map(p => p.id === productId ? { ...p, lead_days: v } : p));
        const { error } = await supabase.from('tn_products').update({ lead_days: v }).eq('id', productId);
        if (error) showSnackbar('No se pudo guardar los días: ' + error.message, 'error');
        else showSnackbar(v == null ? `Usa el default (${defaultLeadDays} días)` : `Días de fabricación: ${v}`, 'success');
    };
    const handleUpdateDefaultLead = async (raw: string) => {
        const v = Math.max(0, Math.round(Number(raw)));
        if (!Number.isFinite(v) || v === defaultLeadDays) return;
        setDefaultLeadDays(v);
        const { error } = await supabase.from('cost_settings').update({ default_lead_days: v }).eq('id', 1);
        if (error) showSnackbar('No se pudo guardar: ' + error.message, 'error');
        else showSnackbar(`Días de fabricación por defecto: ${v} (aplica a pedidos nuevos)`, 'success');
    };

    const handleUpdateProductIsStock = async (productId: number, isStock: boolean) => {
        const { error } = await supabase
            .from('tn_products')
            .update({ is_stock: isStock })
            .eq('id', productId);
        if (!error) {
            setProducts(products.map(p => p.id === productId ? { ...p, is_stock: isStock } : p));
        } else {
            console.error(error);
            showSnackbar('Error al actualizar stock', 'error');
        }
    };

    // Aplica los insumos del editor a TODAS las variantes del producto que tengan el
    // valor elegido (ej: "Smart (RGB Wifi)" → todas las RGB llevan lámpara wifi).
    // Reemplaza la asignación de cada variante destino por la del editor.
    const applyMatsToAllWithValue = async (productId: number, sourceMats: TnVariantMaterial[], value: string) => {
        if (savingMats) return;
        const targets = variants.filter(v => v.product_id === productId && (v.values || []).includes(value));
        if (!targets.length) { showSnackbar('Ninguna variante tiene ese valor', 'error'); return; }
        if (!window.confirm(`¿Aplicar estos ${sourceMats.length} insumo(s) a las ${targets.length} variantes con "${value}"? Reemplaza los insumos específicos que tuvieran.`)) return;
        setSavingMats(true);
        try {
        for (const t of targets) {
            await supabase.from('tn_variant_materials').delete().eq('tn_variant_id', t.id);
            if (sourceMats.length) {
                await supabase.from('tn_variant_materials').insert(sourceMats.map(m => ({
                    tn_variant_id: t.id,
                    material_id: m.material_id,
                    quantity: m.quantity,
                    width_mm: m.width_mm || null,
                    height_mm: m.height_mm || null,
                    thickness_mm: m.thickness_mm || 4,
                })));
            }
        }
        setEditingVariantId(null);
        fetchData();
        showSnackbar(`Insumos aplicados a ${targets.length} variantes con "${value}"`, 'success');
        } finally {
            setSavingMats(false);
        }
    };

    const handleUpdateVariantMaterials = async (variantId: number, newMaterials: TnVariantMaterial[]) => {
        if (savingMats) return;
        setSavingMats(true);
        try {
        await supabase.from('tn_variant_materials').delete().eq('tn_variant_id', variantId);
        if (newMaterials.length > 0) {
            const matsToInsert = newMaterials.map(m => ({
                tn_variant_id: variantId,
                material_id: m.material_id,
                quantity: m.quantity,
                width_mm: m.width_mm || null,
                height_mm: m.height_mm || null,
                thickness_mm: m.thickness_mm || 4,
            }));
            await supabase.from('tn_variant_materials').insert(matsToInsert);
        }
        setEditingVariantId(null);
        fetchData();
        showSnackbar('Insumos de variante actualizados', 'success');
        } finally {
            setSavingMats(false);
        }
    };

    // Editor de insumos de un producto (base) o de una variante. El picker muestra la FOTO
    // del insumo: reconocer el material por imagen es mucho más rápido que leer una lista
    // de cientos de nombres. Los cambios quedan en borrador hasta "Guardar" (se guardan
    // como delete+insert, ver handleUpdate*Materials).
    const renderMaterialEditor = (
        currentMats: TnProductMaterial[] | TnVariantMaterial[],
        setMats: (m: any[]) => void,
        type: 'product' | 'variant',
        parentId: number
    ) => {
        const key = `${type}-${parentId}`;
        const list = currentMats as any[];
        const addPicked = () => {
            const id = matPick[key];
            if (!id) return;
            const mat = materials.find(m => m.id === id);
            const newItem: any = {
                material_id: id,
                quantity: 1,
                thickness_mm: 4,
                width_mm: mat?.type === 'acrylic' ? 100 : undefined,
                height_mm: mat?.type === 'acrylic' ? 100 : undefined,
            };
            if (type === 'product') newItem.tn_product_id = parentId;
            else newItem.tn_variant_id = parentId;
            setMats([...list, newItem]);
            // limpiar la selección: si queda elegida es fácil agregar el mismo insumo dos veces
            setMatPick(prev => ({ ...prev, [key]: null }));
        };
        const setField = (idx: number, field: string, val: number) =>
            setMats(list.map((it, i) => (i === idx ? { ...it, [field]: val } : it)));

        return (
            <div className="grid gap-3">
                <div className="flex flex-wrap items-center gap-2">
                    <PickerSelect
                        className="min-w-[15rem] flex-1"
                        placeholder="Elegí un insumo para agregar…"
                        value={matPick[key] ?? null}
                        onChange={(v) => setMatPick(prev => ({ ...prev, [key]: v == null ? null : Number(v) }))}
                        options={materials.map(m => ({
                            value: m.id,
                            label: m.name,
                            sublabel: m.type === 'acrylic' ? 'Acrílico' : 'Simple',
                            image: materialImg(m),
                        }))}
                    />
                    <Button variant="secondary" aria-label="Crear insumo nuevo" title="Crear insumo nuevo"
                        onClick={() => { setQmTarget({ type, id: parentId }); setShowQuickMaterialModal(true); }}>
                        <Plus size={16} />
                    </Button>
                    <Button disabled={!matPick[key]} onClick={addPicked}><Plus size={16} /> Agregar</Button>
                </div>

                {list.length === 0 ? (
                    <p className="text-[length:var(--text-sm)] text-faint">Sin insumos cargados.</p>
                ) : (
                    <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                        {list.map((item, idx) => {
                            const mat = materials.find(m => m.id === item.material_id);
                            if (!mat) return null;
                            return (
                                <div key={idx} className="rounded-[var(--radius-control)] border border-line bg-card p-3">
                                    <div className="flex items-start gap-2">
                                        {/* misma miniatura que en el picker: se reconoce el insumo sin leer */}
                                        <Thumb src={materialImg(mat)} name={mat.name} size="sm" />
                                        <div className="min-w-0 flex-1">
                                            <div className="truncate text-[length:var(--text-sm)] font-medium text-ink" title={mat.name}>{mat.name}</div>
                                            <div className="text-[length:var(--text-xs)] tabular-nums text-faint">
                                                {fmtUnitCost(mat)} {mat.type === 'acrylic' ? '/ kg' : '/ u'} · {mat.type === 'acrylic' ? 'Acrílico' : 'Simple'}
                                            </div>
                                        </div>
                                        <Button variant="ghost" size="sm" aria-label={`Quitar ${mat.name}`}
                                            onClick={() => setMats(list.filter((_, i) => i !== idx))}>
                                            <Trash2 size={14} />
                                        </Button>
                                    </div>
                                    <div className="mt-3 grid grid-cols-4 gap-2">
                                        <MatPriceField mat={mat} usedIn={matUsage.get(mat.id) || 0}
                                            onSaved={n => setMaterials(ms => ms.map(m => m.id === mat.id ? { ...m, unit_cost: n } : m))} />
                                        {mat.type === 'acrylic' && <>
                                            <NumField label="Espesor" step="0.1" value={item.thickness_mm || 4} onChange={n => setField(idx, 'thickness_mm', n)} />
                                            <NumField label="Ancho" value={item.width_mm || ''} onChange={n => setField(idx, 'width_mm', n)} />
                                            <NumField label="Alto" value={item.height_mm || ''} onChange={n => setField(idx, 'height_mm', n)} />
                                        </>}
                                        <NumField label="Cantidad" value={item.quantity} onChange={n => setField(idx, 'quantity', n)} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    const variantsByProduct = useMemo(() => {
        const map = new Map<number, TnVariant[]>();
        variants.forEach(v => {
            const arr = map.get(v.product_id) || [];
            arr.push(v);
            map.set(v.product_id, arr);
        });
        return map;
    }, [variants]);

    const fmtPrice = (n: number) => {
        return '$' + Math.round(n).toLocaleString('es-AR');
    };

    const computeMetrics = (variant: TnVariant, product: TnProduct, discountPct: number) => {
        // costo directo (reventa) pisa insumos — prioridad: variante > producto.
        // En USD se convierte con el usd_rate de la config.
        const dcOf = (obj: any): number => {
            const val = Number(obj?.direct_cost) || 0;
            if (val <= 0) return 0;
            const cur = obj?.direct_cost_currency;
            return cur === 'USD' ? val * (settings?.usd_rate || 0)
                : cur === 'EUR' ? val * ((settings as any)?.eur_rate || 1800)
                : val;
        };
        const directCost = dcOf(variant) || dcOf(product);
        const baseCost = directCost > 0 ? directCost : calcVariantCost(
            variantMaterials.filter(m => m.tn_variant_id === variant.id),
            productMaterials.filter(m => m.tn_product_id === product.id)
        ).cost;
        const shippingCost = product.has_free_shipping ? 7000 : 0;
        const totalCost = baseCost + shippingCost;
        const price = (variant.promotional_price || variant.price || 0) * (1 - discountPct / 100);
        // Tasas reales (24/08): IIBB = 0 por ahora. MP/Pago Nube 3,5%+IVA (1 pago),
        // en cuotas +10,5%+IVA. Transferencia: 1% TN + 3% Clean. Efectivo: 1% TN.
        const IIBB_RATE = 0;

        let marginCuotas = 0, marginCuotasPct = 0;
        let marginTransf = 0, marginTransfPct = 0;
        let marginEfectivo = 0, marginEfectivoPct = 0;
        let avgMarginPct = 0, roasBE = 0, roasObj = 0;

        if (price > 0) {
            const netRevA = price / 1.21;
            const varCostsA = price * (0.035 + 0.105) * 1.21; // 3 cuotas: 3,5% + 10,5% + IVA
            const iibbA = price * IIBB_RATE;
            marginCuotas = netRevA - varCostsA - iibbA - totalCost;
            marginCuotasPct = (marginCuotas / price) * 100;

            const grossB = price * 0.85; // transferencia = 15% off (igual que efectivo) desde 2026-08-08
            const netRevB = grossB; // transferencia no factura → sin IVA
            const varCostsB = grossB * (0.01 + 0.03); // 1% TN + 3% Clean Managers
            const iibbB = grossB * IIBB_RATE;
            marginTransf = netRevB - varCostsB - iibbB - totalCost;
            marginTransfPct = (marginTransf / price) * 100;

            const grossC = price * 0.85;
            const varCostsC = grossC * 0.01;
            marginEfectivo = grossC - varCostsC - totalCost;
            marginEfectivoPct = (marginEfectivo / price) * 100;

            avgMarginPct = marginCuotasPct * 0.60 + marginTransfPct * 0.25 + marginEfectivoPct * 0.15;
            if (avgMarginPct > 0) {
                roasBE = 1 / (avgMarginPct / 100);
                roasObj = roasBE * 1.8;
            }
        }

        return {
            baseCost, totalCost, price,
            marginCuotas, marginCuotasPct,
            marginTransf, marginTransfPct,
            marginEfectivo, marginEfectivoPct,
            avgMarginPct, roasBE, roasObj
        };
    };

    type VariantMetrics = ReturnType<typeof computeMetrics>;

    const aggregateMetrics = (list: VariantMetrics[]) => {
        const fmtMoneyRange = (min: number, max: number) => {
            if (list.length === 0) return '–';
            if (Math.abs(max - min) < 1) return fmtPrice(min);
            return `${fmtPrice(min)} – ${fmtPrice(max)}`;
        };
        const fmtPctRange = (min: number, max: number) => {
            if (list.length === 0) return '–';
            if (Math.abs(max - min) < 0.05) return `${min.toFixed(1)}%`;
            return `${min.toFixed(1)}% – ${max.toFixed(1)}%`;
        };
        const fmtRoasRange = (min: number, max: number, digits = 2) => {
            if (list.length === 0 || (min === 0 && max === 0)) return '–';
            if (Math.abs(max - min) < 0.05) return `${min.toFixed(digits)}x`;
            return `${min.toFixed(digits)}x – ${max.toFixed(digits)}x`;
        };
        const valid = list.filter(m => m.price > 0);
        const rangeOf = (pick: (m: VariantMetrics) => number) => {
            if (valid.length === 0) return { min: 0, max: 0 };
            const vals = valid.map(pick);
            return { min: Math.min(...vals), max: Math.max(...vals) };
        };
        const cost = rangeOf(m => m.totalCost);
        const price = rangeOf(m => m.price);
        const cuotas = rangeOf(m => m.marginCuotasPct);
        const transf = rangeOf(m => m.marginTransfPct);
        const efect = rangeOf(m => m.marginEfectivoPct);
        const avg = rangeOf(m => m.avgMarginPct);
        const roasBE = rangeOf(m => m.roasBE);
        const roasObj = rangeOf(m => m.roasObj);
        return {
            costStr: valid.length > 0 ? fmtMoneyRange(cost.min, cost.max) : '–',
            priceStr: valid.length > 0 ? fmtMoneyRange(price.min, price.max) : '–',
            cuotasStr: valid.length > 0 ? fmtPctRange(cuotas.min, cuotas.max) : '–',
            transfStr: valid.length > 0 ? fmtPctRange(transf.min, transf.max) : '–',
            efectStr: valid.length > 0 ? fmtPctRange(efect.min, efect.max) : '–',
            avgStr: valid.length > 0 ? fmtPctRange(avg.min, avg.max) : '–',
            roasBEStr: valid.length > 0 ? fmtRoasRange(roasBE.min, roasBE.max) : '–',
            roasObjStr: valid.length > 0 ? fmtRoasRange(roasObj.min, roasObj.max, 1) : '–',
            cuotasPos: cuotas.min >= 0,
            transfPos: transf.min >= 0,
            efectPos: efect.min >= 0,
        };
    };

    const fmtUnitCost = (mat: Material) => {
        const symbol = mat.currency === 'ARS' ? '$' : 'U$D ';
        return `${symbol}${mat.unit_cost.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
    };

    const lastSync = products.length > 0 ? products[0].synced_at : null;

    // ── Filas de la grilla: una por PRODUCTO, con las métricas de sus variantes ya calculadas.
    // Se calcula una vez por cambio de datos (antes se recalculaba todo en cada render).
    type Row = {
        id: number; p: TnProduct; pvs: TnVariant[]; ms: VariantMetrics[];
        agg: ReturnType<typeof aggregateMetrics>;
        noCost: boolean; negative: boolean; minCost: number | null; minPrice: number | null;
        minCuotas: number | null; minTransf: number | null; minEfect: number | null;
    };
    const [fFlag, setFFlag] = useState<Flag | null>(null);

    const allRows: Row[] = useMemo(() => products.map(p => {
        const pvs = variantsByProduct.get(p.id) || [];
        const ms = pvs.map(v => computeMetrics(v, p, productDiscounts[p.id] || 0));
        const priced = ms.filter(m => m.price > 0);
        const min = (pick: (m: VariantMetrics) => number) => (priced.length ? Math.min(...priced.map(pick)) : null);
        return {
            id: p.id, p, pvs, ms,
            agg: aggregateMetrics(ms),
            // sin costo: alguna variante con precio y costo base 0 → su margen está inflado
            noCost: priced.some(m => m.baseCost <= 0),
            negative: priced.some(m => m.marginCuotas < 0),
            minCost: min(m => m.totalCost), minPrice: min(m => m.price),
            minCuotas: min(m => m.marginCuotasPct), minTransf: min(m => m.marginTransfPct), minEfect: min(m => m.marginEfectivoPct),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [products, variantsByProduct, productDiscounts, variantMaterials, productMaterials, materials, settings]);

    const isFlag = (r: Row, f: Flag) =>
        f === 'sin_costo' ? r.noCost : f === 'negativo' ? r.negative : f === 'envio' ? !!r.p.has_free_shipping : !!r.p.is_stock;
    const counts = useMemo(() => {
        const c: Record<Flag, number> = { sin_costo: 0, negativo: 0, envio: 0, stock: 0 };
        for (const r of allRows) (Object.keys(c) as Flag[]).forEach(f => { if (isFlag(r, f)) c[f]++; });
        return c;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allRows]);
    const gridRows = useMemo(() => (fFlag ? allRows.filter(r => isFlag(r, fFlag)) : allRows),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [allRows, fFlag]);

    // Margen promedio del catálogo: solo variantes con precio Y costo cargado (las que no
    // tienen costo darían un margen falso de ~80%).
    const avgMargin = useMemo(() => {
        const ms = allRows.flatMap(r => r.ms).filter(m => m.price > 0 && m.baseCost > 0);
        return ms.length ? ms.reduce((s, m) => s + m.avgMarginPct, 0) / ms.length : 0;
    }, [allRows]);

    // Simulación de costo directo en USD/EUR → ARS, para mostrar al lado del input.
    const directPreview = (cur: string | undefined, raw: unknown) => {
        const val = Number(raw) || 0;
        const rate = cur === 'USD' ? (settings?.usd_rate || 0) : cur === 'EUR' ? ((settings as any)?.eur_rate || 1800) : 0;
        return (cur === 'USD' || cur === 'EUR') && val > 0
            ? <span className="text-[length:var(--text-xs)] tabular-nums text-faint">= ${(val * rate).toLocaleString('es-AR', { maximumFractionDigits: 0 })} ARS</span>
            : null;
    };

    const variantLabel = (v: TnVariant) => v.values.join(', ') || 'Estándar';

    // ── Panel desplegado de un producto: controles, insumos base y variantes ──────────
    const renderExpanded = ({ p, pvs, ms }: Row) => {
        const discountPct = productDiscounts[p.id] || 0;
        const editingBase = editingProductMatsId === p.id;
        return (
            // w-0 + min-w-full: el panel NO aporta ancho mínimo a la tabla de la grilla (si no,
            // su tabla de variantes la ensancha y toda la grilla scrollea de costado); se estira
            // al ancho de la celda y lo que no entra scrollea adentro.
            <div className="grid w-0 min-w-full gap-4 p-4">
                {/* Controles del producto */}
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    <Checkbox label="Envío gratis" description="Suma $7.000 al costo" checked={!!p.has_free_shipping}
                        onCheckedChange={c => handleUpdateProductFreeShipping(p.id, c === true)} />
                    <Checkbox label="En stock" description="Listo el mismo día: no se fabrica" checked={!!p.is_stock}
                        onCheckedChange={c => handleUpdateProductIsStock(p.id, c === true)} />
                    {/* Guarda al salir del campo (o Enter). Define la fecha de listo de los pedidos nuevos. */}
                    <label className={cn('flex items-center gap-2 text-[length:var(--text-sm)] text-soft', p.is_stock && 'opacity-50')}
                        title={p.is_stock ? 'En stock: 0 días' : 'Días corridos para tenerlo listo. Vacío = el default general'}>
                        Días de fabricación
                        <input key={`ld-${p.id}-${p.lead_days ?? ''}`} type="number" min="0" disabled={!!p.is_stock}
                            placeholder={String(defaultLeadDays)} defaultValue={p.lead_days ?? ''}
                            className={cn(CTL, 'w-16 text-right')}
                            onBlur={e => handleUpdateLeadDays(p.id, e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
                    </label>
                    <label className="flex items-center gap-2 text-[length:var(--text-sm)] text-soft" title="Solo simula: no cambia el precio en Tiendanube">
                        Simular descuento
                        <input type="number" min="0" max="100" placeholder="0" className={cn(CTL, 'w-16 text-right')}
                            value={productDiscounts[p.id] || ''}
                            onChange={e => setProductDiscounts(prev => ({ ...prev, [p.id]: parseFloat(e.target.value) || 0 }))} />
                        %
                    </label>
                    <div className="ml-auto flex flex-wrap gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEditProduct(p)}><Pencil size={14} /> Editar producto</Button>
                        <Button variant={editingBase ? 'primary' : 'secondary'} size="sm"
                            title="Insumos que aplican a TODAS las variantes"
                            onClick={() => {
                                setEditingProductMatsId(editingBase ? null : p.id);
                                setEditingProductMats(productMaterials.filter(pm => pm.tn_product_id === p.id) as TnProductMaterial[]);
                            }}>
                            <Layers size={14} /> {editingBase ? 'Cerrar insumos base' : 'Insumos base'}
                        </Button>
                    </div>
                </div>

                {/* Insumos base (aplican a todas las variantes) + costo directo de reventa */}
                {editingBase && (
                    <section className="grid gap-4 rounded-[var(--radius-panel)] border border-line bg-card p-4 animate-in fade-in-0 slide-in-from-top-1">
                        <div>
                            <h3 className="text-[length:var(--text-base)] font-semibold text-ink">Insumos base</h3>
                            <p className="mt-0.5 text-[length:var(--text-sm)] text-faint">Se suman al costo de todas las variantes de este producto.</p>
                        </div>
                        <div className="grid gap-2 rounded-[var(--radius-control)] border border-dashed border-line p-3">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-[length:var(--text-sm)] font-medium text-ink">Costo directo (reventa)</span>
                                <input type="number" min="0" step="any" placeholder="—" className={cn(CTL, 'w-32 text-right')}
                                    value={directCostDraft[p.id] ?? ((p as any).direct_cost != null ? String((p as any).direct_cost) : '')}
                                    onChange={e => setDirectCostDraft(prev => ({ ...prev, [p.id]: e.target.value }))}
                                    onKeyDown={e => e.key === 'Enter' && handleSaveDirectCost(p.id)} />
                                <select className={cn(CTL, 'w-20')}
                                    value={directCurrencyDraft[p.id] ?? ((p as any).direct_cost_currency || 'ARS')}
                                    onChange={e => setDirectCurrencyDraft(prev => ({ ...prev, [p.id]: e.target.value }))}>
                                    <option value="ARS">ARS</option>
                                    <option value="USD">USD</option>
                                    <option value="EUR">EUR</option>
                                </select>
                                {directPreview(directCurrencyDraft[p.id] ?? (p as any).direct_cost_currency, directCostDraft[p.id] ?? (p as any).direct_cost)}
                                <Button variant="secondary" size="sm" onClick={() => handleSaveDirectCost(p.id)}>Guardar costo</Button>
                            </div>
                            <p className="text-[length:var(--text-xs)] text-faint">
                                Para productos que se compran y venden como están: si está cargado, PISA el cálculo por insumos en márgenes, mayorista y resultados. Vaciá el campo para volver a insumos.
                            </p>
                        </div>
                        {renderMaterialEditor(editingProductMats, setEditingProductMats as any, 'product', p.id)}
                        <div className="flex justify-end gap-2 border-t border-line pt-3">
                            <Button variant="secondary" onClick={() => setEditingProductMatsId(null)}>Cancelar</Button>
                            <Button loading={savingMats} onClick={() => handleUpdateProductMaterials(p.id, editingProductMats)}>
                                <Check size={16} /> Guardar insumos base
                            </Button>
                        </div>
                    </section>
                )}

                {/* Variantes con su costo y margen */}
                <div className="overflow-x-auto rounded-[var(--radius-panel)] border border-line bg-card">
                    <table className="w-full min-w-[56rem] text-[length:var(--text-sm)]">
                        <thead>
                            <tr className="border-b border-line text-faint">
                                <th className="h-10 px-4 text-left font-medium">Variante</th>
                                <th className="h-10 px-3 text-right font-medium">Costo</th>
                                <th className="h-10 px-3 text-right font-medium">Precio</th>
                                <th className="h-10 px-3 text-right font-medium" title="Margen con tarjeta en cuotas">Tarjeta</th>
                                <th className="h-10 px-3 text-right font-medium" title="Margen con transferencia (15% off)">Transfer.</th>
                                <th className="h-10 px-3 text-right font-medium" title="Margen en efectivo (15% off, sin IVA)">Efectivo</th>
                                <th className="h-10 px-3 text-right font-medium" title="ROAS de equilibrio">ROAS B.E.</th>
                                <th className="h-10 px-3 text-right font-medium" title="ROAS objetivo (x1,8)">ROAS obj.</th>
                                <th className="h-10 px-3" />
                            </tr>
                        </thead>
                        <tbody>
                            {pvs.map((v, idx) => {
                                const m = ms[idx];
                                const has = m.price > 0;
                                const editingThis = editingVariantId === v.id;
                                return (
                                    <React.Fragment key={v.id}>
                                        <tr className="group border-b border-[var(--border-subtle)] last:border-0 hover:bg-[color-mix(in_oklab,var(--bg-secondary)_55%,transparent)]">
                                            <td className="h-11 px-4">
                                                <div className="flex flex-wrap gap-1">
                                                    {v.values.length > 0
                                                        ? v.values.map((val, i) => <Badge key={i} size="sm">{val}</Badge>)
                                                        : <span className="text-faint">Estándar</span>}
                                                </div>
                                            </td>
                                            <td className="px-3 text-right tabular-nums">
                                                {m.baseCost > 0
                                                    ? <span title={p.has_free_shipping ? `Costo base ${fmtPrice(m.baseCost)} + envío $7.000` : undefined}>{fmtPrice(m.totalCost)}</span>
                                                    : <Badge size="sm" tone="warning">Sin costo</Badge>}
                                            </td>
                                            <td className="px-3 text-right tabular-nums">
                                                {v.promotional_price ? (
                                                    <span className="inline-flex flex-col items-end leading-tight">
                                                        <span className="font-medium text-[var(--danger)]">{fmtPrice(v.promotional_price * (1 - discountPct / 100))}</span>
                                                        <span className="text-[length:var(--text-xs)] text-faint line-through">{fmtPrice((v.price || 0) * (1 - discountPct / 100))}</span>
                                                    </span>
                                                ) : has ? fmtPrice(m.price) : <span className="text-faint">–</span>}
                                            </td>
                                            <td className={cn('px-3 text-right font-medium tabular-nums', marginCls(has, m.marginCuotas >= 0))}>
                                                {has ? <span title={fmtPrice(m.marginCuotas)}>{m.marginCuotasPct.toFixed(1)}%</span> : '–'}
                                            </td>
                                            <td className={cn('px-3 text-right font-medium tabular-nums', marginCls(has, m.marginTransf >= 0))}>
                                                {has ? <span title={fmtPrice(m.marginTransf)}>{m.marginTransfPct.toFixed(1)}%</span> : '–'}
                                            </td>
                                            <td className={cn('px-3 text-right font-medium tabular-nums', marginCls(has, m.marginEfectivo >= 0))}>
                                                {has ? <span title={fmtPrice(m.marginEfectivo)}>{m.marginEfectivoPct.toFixed(1)}%</span> : '–'}
                                            </td>
                                            <td className="px-3 text-right tabular-nums text-soft">{has && m.roasBE > 0 ? `${m.roasBE.toFixed(2)}x` : '–'}</td>
                                            <td className="px-3 text-right font-semibold tabular-nums text-ink">{has && m.roasObj > 0 ? `${m.roasObj.toFixed(1)}x` : '–'}</td>
                                            <td className="px-3">
                                                <div className="flex items-center justify-end gap-1">
                                                    <Button variant={editingThis ? 'primary' : 'secondary'} size="sm"
                                                        title="Cargar / editar los insumos de esta variante"
                                                        onClick={() => {
                                                            setEditingVariantId(editingThis ? null : v.id);
                                                            setEditingVariantMats(variantMaterials.filter(mm => mm.tn_variant_id === v.id) as TnVariantMaterial[]);
                                                        }}>
                                                        {editingThis ? <><X size={14} /> Cerrar</> : <><Pencil size={14} /> Insumos</>}
                                                    </Button>
                                                    {/* El borrado confirma: es irreversible (solo local, no toca Tiendanube). */}
                                                    <Button variant="ghost" size="sm" aria-label={`Eliminar variante ${variantLabel(v)}`}
                                                        title="Eliminar variante (no afecta Tiendanube)"
                                                        className="opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
                                                        onClick={() => handleDeleteVariant(v.id, variantLabel(v))}>
                                                        <Trash2 size={14} />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>

                                        {editingThis && (
                                            <tr className="border-b border-line">
                                                <td colSpan={9} className="bg-muted p-4">
                                                    <section className="grid gap-4 rounded-[var(--radius-panel)] border border-line bg-card p-4 shadow-xs animate-in fade-in-0 slide-in-from-top-1">
                                                        <h3 className="text-[length:var(--text-base)] font-semibold text-ink">Insumos de “{variantLabel(v)}”</h3>
                                                        <div className="grid gap-2 rounded-[var(--radius-control)] border border-dashed border-line p-3">
                                                            <div className="flex flex-wrap items-center gap-2">
                                                                <span className="text-[length:var(--text-sm)] font-medium text-ink">Costo directo de esta variante</span>
                                                                <input type="number" min="0" step="any" placeholder="—" className={cn(CTL, 'w-32 text-right')}
                                                                    value={variantDirectDraft[v.id] ?? ((v as any).direct_cost != null ? String((v as any).direct_cost) : '')}
                                                                    onChange={e => setVariantDirectDraft(prev => ({ ...prev, [v.id]: e.target.value }))}
                                                                    onKeyDown={e => e.key === 'Enter' && handleSaveVariantDirectCost(v.id)} />
                                                                <select className={cn(CTL, 'w-20')}
                                                                    value={variantDirectCurrency[v.id] ?? ((v as any).direct_cost_currency || 'ARS')}
                                                                    onChange={e => setVariantDirectCurrency(prev => ({ ...prev, [v.id]: e.target.value }))}>
                                                                    <option value="ARS">ARS</option>
                                                                    <option value="USD">USD</option>
                                                                    <option value="EUR">EUR</option>
                                                                </select>
                                                                {directPreview(variantDirectCurrency[v.id] ?? (v as any).direct_cost_currency, variantDirectDraft[v.id] ?? (v as any).direct_cost)}
                                                                <Button variant="secondary" size="sm" onClick={() => handleSaveVariantDirectCost(v.id)}>Guardar costo</Button>
                                                            </div>
                                                            <p className="text-[length:var(--text-xs)] text-faint">Pisa el costo directo del producto y los insumos, solo para esta variante.</p>
                                                        </div>
                                                        <div className="grid gap-2">
                                                            <span className="text-[length:var(--text-xs)] font-medium uppercase tracking-wide text-faint">Materiales específicos</span>
                                                            {renderMaterialEditor(editingVariantMats, setEditingVariantMats as any, 'variant', v.id)}
                                                        </div>
                                                        <div className="rounded-[var(--radius-control)] bg-muted px-3 py-2 text-[length:var(--text-sm)] text-soft">
                                                            Costo total estimado: <b className="font-semibold tabular-nums text-ink">
                                                                {fmtPrice(calcVariantCost(editingVariantMats, productMaterials.filter(pm => pm.tn_product_id === p.id)).cost + (p.has_free_shipping ? 7000 : 0))}
                                                            </b>
                                                        </div>
                                                        <div className="flex flex-wrap items-center gap-2 border-t border-line pt-3">
                                                            {(v.values || []).length > 0 && (
                                                                <div className="flex flex-wrap items-center gap-2">
                                                                    <select className={cn(CTL, 'w-auto')} value={applyToValue} onChange={e => setApplyToValue(e.target.value)}>
                                                                        <option value="">Aplicar a todas con…</option>
                                                                        {(v.values || []).map((val: string) => <option key={val} value={val}>{val}</option>)}
                                                                    </select>
                                                                    <Button variant="secondary" size="sm" disabled={!applyToValue || savingMats}
                                                                        onClick={() => applyToValue && applyMatsToAllWithValue(p.id, editingVariantMats, applyToValue)}>
                                                                        Aplicar a todas
                                                                    </Button>
                                                                </div>
                                                            )}
                                                            <div className="ml-auto flex gap-2">
                                                                <Button variant="secondary" onClick={() => setEditingVariantId(null)}>Cancelar</Button>
                                                                <Button loading={savingMats} onClick={() => handleUpdateVariantMaterials(v.id, editingVariantMats)}>
                                                                    <Check size={16} /> Guardar cambios
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </section>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // ── Columnas de la grilla de productos ────────────────────────────────────────────
    const marginCol = (key: string, header: string, pick: (r: Row) => number | null, str: (r: Row) => string, pos: (r: Row) => boolean): GridCol<Row> => ({
        key, header, align: 'right', sort: pick,
        // sin costo cargado el margen es falso (da 65-80%): se muestra apagado, no en verde
        render: r => (
            <span className={cn('whitespace-nowrap font-medium', r.noCost ? 'text-faint' : marginCls(pick(r) != null, pos(r)))}
                title={r.noCost ? 'Margen inflado: alguna variante no tiene costo cargado' : undefined}>{str(r)}</span>
        ),
    });
    const cols: GridCol<Row>[] = [
        { key: 'chev', header: '', width: 'w-10',
            render: r => <ChevronRight size={16} className={cn('text-faint transition-transform', expandedId === r.id && 'rotate-90 text-ink')} /> },
        // sort con el nombre normalizado: el buscador del grid encuentra "lampara" en "Lámpara"
        { key: 'producto', header: 'Producto', sort: r => `${r.p.name} ${norm(r.p.name || '')}`,
            render: ({ p, pvs }) => (
                <div className="flex min-w-[16rem] items-center gap-3">
                    <Thumb src={p.image_url} name={p.name} size="md" />
                    <div className="min-w-0">
                        <div className="max-w-[20rem] truncate font-medium text-ink" title={p.name}>{p.name}</div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[length:var(--text-xs)] text-faint">
                            {pvs.length} variante{pvs.length !== 1 ? 's' : ''}
                            {p.has_free_shipping && <Badge size="sm" tone="info">Envío gratis</Badge>}
                            {p.is_stock && <Badge size="sm" tone="success">En stock</Badge>}
                        </div>
                    </div>
                </div>
            ) },
        { key: 'costo', header: 'Costo', align: 'right', sort: r => r.minCost,
            render: r => (r.noCost && r.minCost != null && r.minCost <= 0
                ? <Badge size="sm" tone="warning">Sin costo</Badge>
                : <span className={cn('whitespace-nowrap', r.noCost && 'text-[var(--warning)]')} title={r.noCost ? 'Alguna variante no tiene costo cargado' : undefined}>{r.agg.costStr}</span>) },
        { key: 'precio', header: 'Precio', align: 'right', sort: r => r.minPrice, render: r => <span className="whitespace-nowrap">{r.agg.priceStr}</span> },
        marginCol('tarjeta', 'Tarjeta', r => r.minCuotas, r => r.agg.cuotasStr, r => r.agg.cuotasPos),
        marginCol('transfer', 'Transfer.', r => r.minTransf, r => r.agg.transfStr, r => r.agg.transfPos),
        marginCol('efectivo', 'Efectivo', r => r.minEfect, r => r.agg.efectStr, r => r.agg.efectPos),
        { key: 'roasbe', header: 'ROAS B.E.', align: 'right', hideOnMobile: true, render: r => <span className="whitespace-nowrap text-soft">{r.agg.roasBEStr}</span> },
        { key: 'roasobj', header: 'ROAS obj.', align: 'right', hideOnMobile: true, render: r => <span className="whitespace-nowrap font-semibold">{r.agg.roasObjStr}</span> },
        // Sin columna de acciones: "Editar producto" está en el panel desplegado. Con ella la
        // tabla pasaba el ancho del contenedor (72px de más) y scrolleaba de costado.
    ];

    // Proveedores: la pantalla vive en Providers.tsx; acá solo se monta a ancho completo.
    if (activeTab === 'providers') return <Providers />;

    const manualCount = products.filter(p => p.id >= 9000000000).length;

    return (
        <div className="tw w-full min-w-0 pb-12 pt-1">
            {/* ── Encabezado ─────────────────────────────────────────────────── */}
            <header className="mb-5 flex flex-wrap items-end justify-between gap-4">
                <div className="min-w-0">
                    <h1 className="text-[length:var(--text-2xl)] font-semibold leading-tight tracking-[var(--tracking-display)] text-ink">Catálogo</h1>
                    <p className="mt-1 max-w-[72ch] text-[length:var(--text-sm)] text-soft">
                        Costo, precio y margen por medio de pago de cada producto. Abrí uno para ver sus variantes y cargarle insumos.
                    </p>
                    {lastSync && (
                        <p className="mt-1 flex items-center gap-1.5 text-[length:var(--text-xs)] text-faint">
                            <CheckCircle size={12} /> Última sincronización con Tiendanube: {new Date(lastSync).toLocaleString('es-AR')}
                        </p>
                    )}
                    <label className="mt-1 flex items-center gap-1.5 text-[length:var(--text-xs)] text-faint"
                        title="Para productos sin días propios. Fecha de listo = compra + el producto más lento del pedido (stock = 0).">
                        Días de fabricación por defecto
                        <input key={`dld-${defaultLeadDays}`} type="number" min="0" defaultValue={defaultLeadDays}
                            className={cn(CTL, 'h-7 w-14 text-right')}
                            onBlur={e => handleUpdateDefaultLead(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
                    </label>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button variant="secondary" onClick={() => setShowNewProductModal(true)}><Plus size={16} /> Producto manual</Button>
                    <Button loading={syncing} onClick={handleSync}><RefreshCw size={16} /> Sincronizar productos</Button>
                </div>
            </header>

            {/* ── Resumen ─────────────────────────────────────────────────────── */}
            <section className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
                <MetricCard label="Productos" value={products.length} locale="es-AR"
                    context={`${variants.length} variantes${manualCount ? ` · ${manualCount} manuales` : ''}`} />
                <MetricCard label="Sin costo cargado" value={counts.sin_costo} locale="es-AR"
                    context="Su margen está inflado: faltan insumos o costo directo" />
                <MetricCard label="Margen negativo" value={counts.negativo} locale="es-AR"
                    context="Pierden plata vendiendo con tarjeta en cuotas" />
                <MetricCard label="Margen promedio" value={Math.round(avgMargin)} suffix="%" locale="es-AR"
                    context="Con costo cargado · 60% tarjeta, 25% transf., 15% efectivo" />
            </section>

            {/* ── Grilla de productos ──────────────────────────────────────────
                Una fila por producto; al abrirla se ven sus variantes con costo y margen,
                los controles del producto y los editores de insumos. */}
            <DataGrid
                rows={gridRows}
                cols={cols}
                loading={loading && products.length === 0}
                rowKey={r => r.id}
                pageSize={50}
                searchPlaceholder="Buscar producto…"
                emptyTitle="No hay productos sincronizados"
                emptyText="Traé el catálogo desde Tiendanube para ver costos y márgenes."
                emptyAction={<Button size="sm" loading={syncing} onClick={handleSync}><RefreshCw size={14} /> Sincronizar desde Tiendanube</Button>}
                filtersActive={fFlag != null}
                onClearFilters={() => setFFlag(null)}
                // la grilla abre/cierra la fila; acá se espeja para rotar el chevron
                onRowClick={r => setExpandedId(id => (id === r.id ? null : r.id))}
                renderExpanded={renderExpanded}
                toolbar={
                    <ChipGroup label="Filtros rápidos" multiple={false}
                        value={fFlag ? [fFlag] : []}
                        onValueChange={v => setFFlag((v[0] as Flag) ?? null)}
                        options={[
                            { value: 'sin_costo', label: `Sin costo · ${counts.sin_costo}` },
                            { value: 'negativo', label: `Margen negativo · ${counts.negativo}` },
                            { value: 'envio', label: `Envío gratis · ${counts.envio}` },
                            { value: 'stock', label: `En stock · ${counts.stock}` },
                        ]} />
                }
            />

            {/* ── Crear insumo rápido (desde el editor de insumos) ─────────────── */}
            <Dialog open={showQuickMaterialModal} onOpenChange={setShowQuickMaterialModal}>
                <DialogContent className="tw !w-[min(calc(100vw_-_2rem),520px)]" title="Crear insumo"
                    description="Queda en Insumos y se agrega al editor desde donde lo creaste.">
                    <div className="grid gap-4">
                        <Input label="Nombre" placeholder="Ej: Bisagra reforzada" autoFocus value={qmName} onChange={e => setQmName(e.target.value)} />
                        <div className="grid gap-4 sm:grid-cols-2">
                            <Field label="Tipo">
                                <select className={SELECT} value={qmType} onChange={e => setQmType(e.target.value as 'simple' | 'acrylic')}>
                                    <option value="simple">Simple (unidad / metro)</option>
                                    <option value="acrylic">Acrílico (plancha / kg)</option>
                                </select>
                            </Field>
                            <Field label="Proveedor">
                                {!qmNewProvOpen ? (
                                    <div className="flex gap-2">
                                        <select className={SELECT} value={qmProvider} onChange={e => setQmProvider(e.target.value)}>
                                            <option value="">Sin proveedor</option>
                                            {providers.map(pr => <option key={pr.id} value={pr.id}>{pr.name}</option>)}
                                        </select>
                                        <Button variant="secondary" aria-label="Nuevo proveedor" title="Nuevo proveedor" onClick={() => setQmNewProvOpen(true)}><Plus size={16} /></Button>
                                    </div>
                                ) : (
                                    <div className="flex gap-2">
                                        <input className={SELECT} autoFocus placeholder="Nombre del proveedor" value={qmNewProvName}
                                            onChange={e => setQmNewProvName(e.target.value)} onKeyDown={e => e.key === 'Enter' && addProviderInlineQm()} />
                                        <Button aria-label="Crear proveedor" onClick={addProviderInlineQm}><Check size={16} /></Button>
                                        <Button variant="ghost" aria-label="Cancelar" onClick={() => { setQmNewProvOpen(false); setQmNewProvName(''); }}><X size={16} /></Button>
                                    </div>
                                )}
                            </Field>
                        </div>
                        <div className="grid grid-cols-[1fr_7rem] gap-4">
                            <Input label={qmType === 'acrylic' ? 'Costo por kg' : 'Costo unitario'} type="number" placeholder="0" value={qmCost} onChange={e => setQmCost(e.target.value)} />
                            <Field label="Moneda">
                                <select className={SELECT} value={qmCurrency} onChange={e => setQmCurrency(e.target.value as 'ARS' | 'USD')}>
                                    <option value="ARS">ARS</option>
                                    <option value="USD">USD</option>
                                </select>
                            </Field>
                        </div>
                        <div className="mt-2 flex justify-end gap-2">
                            <Button variant="secondary" onClick={() => setShowQuickMaterialModal(false)}>Cancelar</Button>
                            <Button disabled={!qmName || !qmCost} onClick={handleSaveQuickMaterial}>Crear insumo</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* ── Alta de producto manual ──────────────────────────────────────── */}
            <Dialog open={showNewProductModal} onOpenChange={setShowNewProductModal}>
                <DialogContent className="tw !w-[min(calc(100vw_-_2rem),680px)]" title="Producto manual"
                    description="Para productos que no están en Tiendanube. Solo existen en el dashboard.">
                    <div className="grid gap-4">
                        <Input label="Nombre del producto" placeholder="Ej: Lámpara Cilindro Grande" value={npName} onChange={e => setNpName(e.target.value)} />
                        <Input label="URL de la imagen (opcional)" placeholder="https://…" value={npImageUrl} onChange={e => setNpImageUrl(e.target.value)} />
                        <VariantsEditor rows={npVariants} minRows={1} onChange={rows => setNpVariants(rows as typeof npVariants)} />
                        <div className="mt-2 flex justify-end gap-2">
                            <Button variant="secondary" onClick={() => setShowNewProductModal(false)}>Cancelar</Button>
                            <Button onClick={handleAddProduct}>Crear producto</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* ── Edición de producto ──────────────────────────────────────────── */}
            <Dialog open={!!editingProduct} onOpenChange={o => { if (!o) setEditingProduct(null); }}>
                <DialogContent className="tw !w-[min(calc(100vw_-_2rem),720px)]" title="Editar producto"
                    description="Cambios locales del dashboard: no se suben a Tiendanube.">
                    <div className="grid gap-4">
                        <Input label="Nombre del producto" value={epName} onChange={e => setEpName(e.target.value)} />
                        <Input label="URL de la imagen" placeholder="https://…" value={epImageUrl} onChange={e => setEpImageUrl(e.target.value)} />
                        <VariantsEditor rows={epVariants} onChange={rows => setEpVariants(rows as typeof epVariants)} />
                        <div className="mt-2 flex justify-end gap-2">
                            <Button variant="secondary" onClick={() => setEditingProduct(null)}>Cancelar</Button>
                            <Button onClick={handleSaveEditProduct}>Guardar cambios</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
